import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { FileSpreadsheet as Sheet, Loader2, Eye, RefreshCw, Save, CheckCircle2, MailX } from 'lucide-react';
import { toast } from 'sonner';
import { SheetSyncResult } from './SheetSyncResult';

export function GoogleSheetSyncDialog({ api, onSynced }) {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [saJson, setSaJson] = useState('');
  const [busy, setBusy] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) api.get('/api/admin/sheet-sync/config').then(({ data }) => setCfg(data)).catch(() => toast.error('Could not load sync settings'));
  }, [open, api]);

  const save = async () => {
    setBusy('save');
    try {
      const { data } = await api.put('/api/admin/sheet-sync/config', {
        spreadsheet_url: cfg.spreadsheet_url, merchant_list_tab: cfg.merchant_list_tab, updates_tab: cfg.updates_tab,
        bounced_tab: cfg.bounced_tab, log_bounces: cfg.log_bounces,
        ...(saJson.trim() ? { service_account_json: saJson } : {}),
      });
      setCfg(data); setSaJson('');
      toast.success('Sheet settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setBusy('');
  };

  const run = async (dryRun) => {
    if (!dryRun && !window.confirm('Apply updated emails to the database, delete processed rows from the updates tab, and refresh the Merchant List tab?')) return;
    setBusy(dryRun ? 'preview' : 'run');
    try {
      const { data } = await api.post('/api/admin/sheet-sync/run', { dry_run: dryRun });
      setResult(data);
      if (!dryRun) {
        toast.success(`Synced: ${data.email_updates.updated} email(s) updated, ${data.merchant_list.rows_updated} rows refreshed, ${data.merchant_list.rows_added} added`);
        onSynced?.();
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); }
    setBusy('');
  };

  const pushBounces = async () => {
    setBusy('bounces');
    try {
      const { data } = await api.post('/api/admin/sheet-sync/flush-bounces', { include_existing: true });
      data.ok ? toast.success(`Added ${data.added} bounced email(s) to "${cfg.bounced_tab}"${data.already_listed ? ` (${data.already_listed} already listed)` : ''}`) : toast.error(data.skipped);
    } catch (e) { toast.error(e.response?.data?.detail || 'Push failed'); }
    setBusy('');
  };

  const field = (k, label, testId) => (
    <div className="space-y-1">
      <Label className="text-[#A0B4CC] text-xs">{label}</Label>
      <Input value={cfg?.[k] || ''} onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })}
        className="bg-[#000B18] border-white/10 text-white" data-testid={testId} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-[#00E676]/40 text-[#00E676] hover:bg-[#00E676]/10" data-testid="sheet-sync-open-btn">
          <Sheet className="w-4 h-4 mr-2" />Google Sheet Sync
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#041A3B] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="sheet-sync-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Sheet className="w-5 h-5 text-[#00E676]" />Google Sheet Sync</DialogTitle></DialogHeader>
        {!cfg ? <Loader2 className="w-5 h-5 animate-spin text-[#00E5FF]" /> : (
          <div className="space-y-4">
            <p className="text-xs text-[#A0B4CC]">
              1) Rows in <b>{cfg.updates_tab}</b> with an <b>Updated Email</b> are matched to merchants (by email, then business name), the email is updated, and the row is deleted. Rows that can't be applied stay and get a reason in “Is the Email Updated?”.
              {' '}2) <b>{cfg.merchant_list_tab}</b> is refreshed with every merchant; your extra columns (J onward) are kept.
            </p>
            {field('spreadsheet_url', 'Spreadsheet URL', 'sheet-sync-url-input')}
            <div className="grid grid-cols-3 gap-3">
              {field('merchant_list_tab', 'Merchant list tab', 'sheet-sync-list-tab-input')}
              {field('updates_tab', 'Updated emails tab', 'sheet-sync-updates-tab-input')}
              {field('bounced_tab', 'Bounced emails tab', 'sheet-sync-bounced-tab-input')}
            </div>
            <div className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-[#000B18]/60 p-3">
              <div>
                <p className="text-sm">Log bounced emails to the sheet</p>
                <p className="text-xs text-[#5C7599]">When an email hard-bounces (or keeps soft-bouncing / has no mail server), the person's details plus the bounce reason are added to “{cfg.bounced_tab}” within ~2 minutes. Each address is listed once.</p>
              </div>
              <Switch checked={!!cfg.log_bounces} onCheckedChange={(v) => setCfg({ ...cfg, log_bounces: v })} data-testid="sheet-sync-log-bounces-switch" />
            </div>
            <div className="space-y-1">
              <Label className="text-[#A0B4CC] text-xs">Service account JSON</Label>
              {cfg.credentials_configured && (
                <p className="text-xs text-[#00E676] flex items-center gap-1" data-testid="sheet-sync-credentials-status">
                  <CheckCircle2 className="w-3 h-3" />Connected as {cfg.client_email}. The sheet must be shared with this email as Editor.
                </p>
              )}
              <Textarea rows={3} value={saJson} onChange={(e) => setSaJson(e.target.value)}
                placeholder={cfg.credentials_configured ? 'Paste a new key only to replace the saved one' : '{ "type": "service_account", ... }'}
                className="bg-[#000B18] border-white/10 text-white font-mono text-xs" data-testid="sheet-sync-sa-json-input" />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={save} disabled={!!busy} variant="ghost" className="text-[#00E5FF]" data-testid="sheet-sync-save-btn">
                {busy === 'save' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Save settings
              </Button>
              <Button onClick={pushBounces} disabled={!!busy || !cfg.credentials_configured} variant="outline" className="border-[#FF9100]/40 text-[#FF9100]" data-testid="sheet-sync-push-bounces-btn">
                {busy === 'bounces' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MailX className="w-4 h-4 mr-2" />}Push bounced emails now
              </Button>
              <Button onClick={() => run(true)} disabled={!!busy || !cfg.credentials_configured} variant="outline" className="border-white/20 text-white" data-testid="sheet-sync-preview-btn">
                {busy === 'preview' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}Preview
              </Button>
              <Button onClick={() => run(false)} disabled={!!busy || !cfg.credentials_configured} className="bg-[#00E676] text-[#000B18] hover:bg-[#5BFFA0]" data-testid="sheet-sync-run-btn">
                {busy === 'run' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}Run sync
              </Button>
            </div>
            {result && <SheetSyncResult result={result} />}
            {!result && cfg.last_run && (
              <p className="text-xs text-[#5C7599]" data-testid="sheet-sync-last-run">
                Last sync {new Date(cfg.last_run.ran_at).toLocaleString()} by {cfg.last_run.by}: {cfg.last_run.email_updates?.updated ?? 0} email(s) updated, {cfg.last_run.merchant_list?.rows_added ?? 0} rows added.
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
